/**
 * wl-theme main JavaScript
 * Sticky header, mobile menu, scroll reveal, smooth scroll, active nav
 *
 * @package wl-theme
 */
(function() {
    'use strict';

    // =========================================================================
    // 1. STICKY HEADER
    // Adds .header--scrolled when page is scrolled past 50px
    // On mobile: hides on scroll down, shows on scroll up
    // =========================================================================
    var header = document.querySelector('.header');
    var ticking = false;
    var lastScrollY = 0;
    var mobileBreakpoint = 768;

    function updateHeader() {
        if (!header) return;
        var currentScrollY = window.scrollY;

        // Scrolled state (background change)
        if (currentScrollY > 50) {
            header.classList.add('header--scrolled');
        } else {
            header.classList.remove('header--scrolled');
            header.classList.remove('header--hidden');
            lastScrollY = currentScrollY;
            ticking = false;
            return;
        }

        // Hide on scroll down, show on scroll up
        var delta = currentScrollY - lastScrollY;

        if (delta > 8 && currentScrollY > 100) {
            header.classList.add('header--hidden');
        } else if (delta < -5) {
            header.classList.remove('header--hidden');
        }

        lastScrollY = currentScrollY;
        ticking = false;
    }

    if (header) {
        window.addEventListener('scroll', function() {
            if (!ticking) {
                requestAnimationFrame(updateHeader);
                ticking = true;
            }
        });

        // Run once on load in case page is already scrolled
        updateHeader();
    }

    // =========================================================================
    // 2. MOBILE MENU
    // Toggle, close, ESC, overlay click, focus trap, link click close
    // =========================================================================
    var menuToggle = document.querySelector('[data-testid="mobile-menu-toggle"]');
    var menuClose = document.querySelector('[data-testid="mobile-menu-close"]');
    var mobileMenu = document.querySelector('[data-testid="mobile-menu"]');

    if (menuToggle && mobileMenu) {
        function openMenu() {
            mobileMenu.classList.add('mobile-menu--open');
            mobileMenu.setAttribute('aria-hidden', 'false');
            menuToggle.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';

            // Focus first link for a11y
            var firstLink = mobileMenu.querySelector('a');
            if (firstLink) {
                firstLink.focus();
            }
        }

        function closeMenu() {
            mobileMenu.classList.remove('mobile-menu--open');
            mobileMenu.setAttribute('aria-hidden', 'true');
            menuToggle.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
            menuToggle.focus();
        }

        menuToggle.addEventListener('click', openMenu);

        if (menuClose) {
            menuClose.addEventListener('click', closeMenu);
        }

        // Close on ESC key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && mobileMenu.classList.contains('mobile-menu--open')) {
                closeMenu();
            }
        });

        // Close on overlay click (click on the backdrop, not menu content)
        mobileMenu.addEventListener('click', function(e) {
            if (e.target === mobileMenu) {
                closeMenu();
            }
        });

        // Focus trap - keep Tab cycling within menu
        mobileMenu.addEventListener('keydown', function(e) {
            if (e.key !== 'Tab') return;

            var focusable = mobileMenu.querySelectorAll(
                'a, button, input, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) return;

            var first = focusable[0];
            var last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        });

        // Close menu when a link is clicked (single-page navigation)
        var menuLinks = mobileMenu.querySelectorAll('a');
        menuLinks.forEach(function(link) {
            link.addEventListener('click', closeMenu);
        });
    }

    // =========================================================================
    // 3. SCROLL REVEAL
    // IntersectionObserver-based reveal animations
    // Respects prefers-reduced-motion
    // =========================================================================
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        var revealSelectors = '.reveal, .reveal--left, .reveal--right, .reveal--scale, .reveal-stagger';
        var revealElements = document.querySelectorAll(revealSelectors);

        if (revealElements.length > 0) {
            var revealObserver = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        revealObserver.unobserve(entry.target);
                    }
                });
            }, {
                threshold: 0.15,
                rootMargin: '0px 0px -40px 0px'
            });

            revealElements.forEach(function(el) {
                revealObserver.observe(el);
            });

            // Hero animates immediately (already in viewport on load)
            var heroContent = document.querySelector('.hero .reveal');
            if (heroContent) {
                setTimeout(function() {
                    heroContent.classList.add('is-visible');
                }, 200);
            }

            var heroStats = document.querySelector('.hero .reveal-stagger');
            if (heroStats) {
                setTimeout(function() {
                    heroStats.classList.add('is-visible');
                }, 600);
            }
        }
    }

    // =========================================================================
    // 4. SMOOTH SCROLL
    // For anchor links (href="#section"), accounts for sticky header height
    // =========================================================================
    document.querySelectorAll('a[href^="#"]').forEach(function(link) {
        link.addEventListener('click', function(e) {
            var href = this.getAttribute('href');
            if (!href || href === '#') return;

            var target = document.querySelector(href);
            if (target) {
                e.preventDefault();
                var headerHeight = header ? header.offsetHeight : 0;
                var targetPos = target.getBoundingClientRect().top + window.scrollY - headerHeight;
                window.scrollTo({
                    top: targetPos,
                    behavior: 'smooth'
                });
            }
        });
    });

    // =========================================================================
    // 5. ACTIVE NAV LINK
    // Highlights current page in navigation based on pathname
    // =========================================================================
    var currentPath = window.location.pathname;
    document.querySelectorAll('.header__nav a, .mobile-menu__nav a').forEach(function(link) {
        if (link.getAttribute('href') === currentPath) {
            link.classList.add('is-active');
        }
    });

    // =========================================================================
    // 6. ARTICLES CAROUSEL (prev/next arrows)
    // Scrolls the articles carousel by one card width
    // =========================================================================
    var articlesCarousel = document.querySelector('.articles-carousel');
    var prevBtn = document.querySelector('[data-carousel-prev]');
    var nextBtn = document.querySelector('[data-carousel-next]');

    if (articlesCarousel && prevBtn && nextBtn) {
        var getScrollAmount = function() {
            var card = articlesCarousel.querySelector('.article-card');
            if (!card) return 300;
            var gap = parseFloat(getComputedStyle(articlesCarousel).gap) || 24;
            return card.offsetWidth + gap;
        };

        prevBtn.addEventListener('click', function() {
            articlesCarousel.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
        });

        nextBtn.addEventListener('click', function() {
            articlesCarousel.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
        });
    }

    // =========================================================================
    // 7. BRANCHES CAROUSEL DOTS
    // Updates active dot on scroll for the branches section
    // =========================================================================
    var branchesContainer = document.querySelector('.branches');
    var branchesDots = document.querySelectorAll('.branches-dot');

    if (branchesContainer && branchesDots.length) {
        // Click dots to scroll to card
        branchesDots.forEach(function(dot, index) {
            dot.addEventListener('click', function() {
                var cards = branchesContainer.querySelectorAll('.branch-card');
                if (cards[index]) {
                    cards[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                }
            });
        });

        // Update dots on scroll
        branchesContainer.addEventListener('scroll', function() {
            var cards = branchesContainer.querySelectorAll('.branch-card');
            if (!cards.length) return;

            var containerRect = branchesContainer.getBoundingClientRect();
            var centerX = containerRect.left + containerRect.width / 2;
            var closest = 0;
            var closestDist = Infinity;

            cards.forEach(function(card, i) {
                var cardRect = card.getBoundingClientRect();
                var cardCenter = cardRect.left + cardRect.width / 2;
                var dist = Math.abs(cardCenter - centerX);
                if (dist < closestDist) {
                    closestDist = dist;
                    closest = i;
                }
            });

            branchesDots.forEach(function(dot, i) {
                dot.classList.toggle('branches-dot--active', i === closest);
            });
        }, { passive: true });
    }

})();
